<template>
  <advanced-table
    ref="archiveTable"
    :headers="headers"
    :filters="filters"
    :sortBy="'startDate'"
    :descending="true"
    :resource="resource"
    :optionLists="optionLists"
    :customQueryFilter="generateCustomQueryFilter"
    :queryExtras="getRouteQueryExtras"
  />
</template>

<script>
import AdvancedTable from "@/components/AdvancedTable.vue";
import { format } from "date-fns";

// Types that should be hidden from the filter and absorbed under a master type.
// Matching is done by `title` (singular) against the backend event type records.
const TYPE_MERGE_GROUPS = [
  { master: "אחר",   absorb: ["העברה לטיפול צוות אחר", "סיוע לרכב תקוע"] },
  { master: "איתור", absorb: ["מנותק קשר"] },
  { master: "חילוץ", absorb: ["אסון טבע", 'אר"ן', "מצב רפואי הדורש פינוי"] },
];

export default {
  data() {
    return {
      headers: [
        {
          text: "מזהה אירוע",
          value: "_id",
          align: "right",
          visible: false,
          width: "200",
          displayFunc: (data, key) => {
            return data[key];
          }
        },
        {
          text: "כותרת",
          value: "title",
          align: "right",
          visible: true,
          width: "200",
          displayFunc: (data, key) => {
            return data[key];
          },
          linkTo: item => {
            return `/events/${item._id}`;
          }
        },
        {
          text: "כמות מחולצים",
          value: "customersCount",
          align: "right",
          visible: true,
          sortable: false,
          displayFunc: (data, key) => {
            const eventId = data._id || data.id;
            const byEvent = this.$store.getters["customers/eventCustomers"];
            return typeof byEvent === "function" ? byEvent(eventId).length : 0;
          }
        },
        {
          text: "זמן התחלה",
          value: "startDate",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            return format(data[key], "DD-MM-YYYY HH:mm");
          }
        },
        {
          text: "זמן סיום",
          value: "endDate",
          align: "right",
          visible: false,
          displayFunc: (data, key) => {
            return data[key] ? format(data[key], "DD-MM-YYYY HH:mm") : 'אירוע פעיל';
          }
        },
        {
          text: "דו״חות הראל",
          value: "includedInHarelReports",
          align: "right",
          visible: false,
          displayFunc: (data, key) => {
            return data[key] ? 'כן' : 'לא';
          }
        },
        {
          text: "תחקיר",
          value: "debriefing",
          align: "right",
          visible: false,
          displayFunc: (data, key) => {
            return data[key] ? 'כן' : 'לא';
          }
        },
        {
          text: "תאור האירוע",
          value: "description",
          align: "right",
          visible: false,
          displayFunc: (data, key) => {
            return data[key] ? data[key] : 'ללא תאור';
          }
        },
        {
          text: "סוג",
          value: "type",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            let eventType = this.entityById("eventTypes", data[key] ? data[key]._id : data[key]);
            return eventType ? eventType['title'] : 'ללא סוג';
          }
        },
        {
          text: "תת סוג",
          value: "subType",
          align: "right",
          visible: false,
          displayFunc: (data, key) => {
            let eventSubType = this.entityById("eventSubTypes", data[key] ? data[key]._id : data[key]);
            return eventSubType ? eventSubType['title'] : 'ללא תת סוג';
          }
        },
        {
          text: "מקור",
          value: "source",
          align: "right",
          visible: false,
          displayFunc: (data, key) => {
            let eventSource = this.entityById("eventSources", data[key] ? data[key]._id : data[key]);
            return eventSource ? eventSource['title'] : 'ללא מקור';
          }
        },
        {
          text: "אופן פינוי",
          value: "evacuationType",
          align: "right",
          visible: false,
          displayFunc: (data, key) => {
            let evacuationType = this.entityById("evacuationTypes", data[key] ? data[key]._id : data[key]);
            return evacuationType ? evacuationType['title'] : 'לא ידוע';
          }
        },
        {
          text: "שם מחולץ ראשי",
          value: "mainCustomerName",
          align: "right",
          visible: false,
          sortable: false,
          displayFunc: (data, key) => {
            console.log(data)
            let id = data['mainCustomer'] ? data['mainCustomer']._id || data['mainCustomer'] : null;
            let customer = this.$store.state.entities.customers[id];
            return customer ? customer['name'] : 'לא ידוע';
          }
        },
        {
          text: "פוליסה מחולץ ראשי",
          value: "mainCustomerPolicy",
          align: "right",
          visible: false,
          sortable: false,
          displayFunc: (data, key) => {
            let id = data['mainCustomer'] ? data['mainCustomer']._id || data['mainCustomer'] : null;
            let customer = this.$store.state.entities.customers[id];
            return customer ? customer['policyNumber'] : 'לא ידוע';
          }
        },
        {
          text: "ת״ז מחולץ ראשי",
          value: "mainCustomerId",
          align: "right",
          visible: false,
          sortable: false,
          displayFunc: (data, key) => {
            let id = data['mainCustomer'] ? data['mainCustomer']._id || data['mainCustomer'] : null;
            let customer = this.$store.state.entities.customers[id];
            return customer ? customer['id'] : 'לא ידוע';
          }
        },
        {
          text: "מדינות",
          value: "countries",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            let field = "name";
            let p = {};
            let array = data[key];
            if (Array.isArray(array) && array.length) {
              return array
                .map(item => {
                  let country = this.entityById("countries", item._id || item);
                  return country ? country[field] : 'ללא שם';
                })
                .reduce((p, v) => p + ", " + v);
            }
            return "";
          }
        },
        {
          text: "איזורים",
          value: "regions",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            let field = "name";
            let p = {};
            let array = data[key];
            if (Array.isArray(array) && array.length) {
              return array
                .map(item => {
                  let region = this.entityById("regions", item._id || item);
                  return region ? region[field] : 'ללא שם';
                })
                .reduce((p, v) => p + ", " + v);
            }
            return "כל האיזורים";
          }
        },
        {
          text: "ארן",
          value: "aran",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            if (data[key]) {
              let aran = this.$store.state.entities.arans[data[key]];
              return aran ? aran.name : 'ללא שם';
            }
            return "";
          }
        }
      ],
      filters: [
        {
          type: "COMBOBOX",
          label: "מדינות",
          model: "countries",
          items: "countries",
          itemText: "name",
          itemValue: "_id",
          active: true
        },
        {
          type: "COMBOBOX",
          label: "סוגי אירועים",
          model: "typesFilter",
          items: "typesFilter",
          itemText: "titlePlural",
          itemValue: "_id",
          active: true
        },
        {
          type: "COMBOBOX",
          label: "תת סוגי אירועים",
          model: "subTypes",
          items: "subTypes",
          itemText: "title",
          itemValue: "_id",
          active: false
        },
        {
          type: "COMBOBOX",
          label: "אופני פינוי",
          model: "evacuationTypes",
          items: "evacuationTypes",
          itemText: "title",
          itemValue: "_id",
          active: false
        },
        {
          type: "DATE",
          label: "התחיל אחרי",
          model: "startDateAfter",
          active: false
        },
        {
          type: "DATE",
          label: "התחיל לפני",
          model: "startDateBefore",
          active: false
        },
        {
          type: "BOOLEAN",
          label: "תחקיר",
          model: "debriefing",
          active: false
        },
        {
          type: "BOOLEAN",
          label: "דו״חות הראל",
          model: "includedInHarelReports",
          active: false
        },
    {
      type: "TEXT",
      label: "חיפוש חופשי",
      model: "search",
      icon: "search",
      expanded: true,
      active: true
    }
      ],
    };
  },
  computed: {
    events() {
      return this.$store.getters["entityList"]("events");
    },
    types() {
      return this.$store.getters["entityList"]("eventTypes");
    },
    subTypes() {
      return this.$store.getters["entityList"]("eventSubTypes");
    },
    evacuationTypes() {
      return this.$store.getters["entityList"]("evacuationTypes");
    },
    countries() {
      return this.$store.getters["entityList"]("countries").filter(item => {
        return item.isActive;
      });
    },
    resource() {
      return this.$store.state.resources["event"];
    },
    mergedTypes() {
      const absorbedTitles = new Set(
        TYPE_MERGE_GROUPS.flatMap(g => g.absorb)
      );
      return this.types.filter(t => !absorbedTitles.has(t.title));
    },
    optionLists() {
      return {
        types: this.types,
        typesFilter: this.mergedTypes,
        subTypes: this.subTypes,
        evacuationTypes: this.evacuationTypes,
        countries: this.countries
      }
    }
  },
  methods: {
    entityById(entityType, id) {
      return this.$store.getters["entity"](entityType, id);
    },
    fetchCustomersIfNeeded() {
      const customers =
        this.$store.state.entities &&
        this.$store.state.entities.customers
          ? this.$store.state.entities.customers
          : {};
      if (Object.keys(customers).length > 0) {
        return Promise.resolve();
      }
      return this.$store.dispatch("API_GET", {
        url: "/customers",
        entityType: "customers",
        schema: "customerListSchema",
        params: { limit: 10000 },
        replace: false
      });
    },
    generateCustomQueryFilter() {
      const result = {};

      // _id filter (from route query)
      const rawId = this.$route.query._id;
      if (rawId) {
        const ids = rawId.split(",").map(id => id.trim()).filter(Boolean);
        if (ids.length) {
          result._id = { $in: ids };
        }
      }

      // Expand merged type selections to include absorbed type IDs
      const selectedTypes =
        this.$refs.archiveTable &&
        this.$refs.archiveTable.filterStatus &&
        this.$refs.archiveTable.filterStatus.typesFilter;
      if (selectedTypes && selectedTypes.length) {
        const allTypeIds = [];
        selectedTypes.forEach(selected => {
          allTypeIds.push(selected._id);
          const group = TYPE_MERGE_GROUPS.find(g => g.master === selected.title);
          if (group) {
            group.absorb.forEach(absorbedTitle => {
              const match = this.types.find(t => t.title === absorbedTitle);
              if (match) allTypeIds.push(match._id);
            });
          }
        });
        result.type = { $in: allTypeIds };
      }

      return Object.keys(result).length ? result : null;
    }
    ,
    getRouteQueryExtras() {
      const { _id, q } = this.$route.query;
      const extras = {};
      if (_id) extras._id = _id;
      if (q) extras.q = q;
      return Object.keys(extras).length ? extras : null;
    }
  },
  mounted() {
    this.fetchCustomersIfNeeded();
  },
  watch: {
    "$route.query._id": {
      handler() {
        if (this.$refs.archiveTable) {
          this.$refs.archiveTable.getDataFromApi();
        }
      }
    }
    ,
    "$route.query.q": {
      handler() {
        if (this.$refs.archiveTable) {
          this.$refs.archiveTable.getDataFromApi();
        }
      }
    }
  },
  components: {
    AdvancedTable
  }
};
</script>

<style scoped>
</style>


// WEBPACK FOOTER //
// src/components/events/archive/EventsArchive.vue